import { useContext, useMemo } from 'react';
import UserContext from '@/context/user';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Avatar,
  ListItemButton,
  ListItemIcon,
  styled,
  Switch,
} from '@mui/material';
import { SideBar as PSideBar } from 'pragmatic-ui';
import ApplicationLogo from '@/components/ApplicationLogo';
import TeamIcon from '@/components/icons/TeamIcon';
import LogoutIcon from '@/components/icons/LogoutIcon';
import VehicleIcon from '@/components/icons/VehicleIcon';
import PermitsIcon from '@/components/icons/PermitsIcon';
import GMapIcon from '@/components/icons/GMapIcon';
import { useLogout } from '@/hooks/auth/useAuth';
import { useColorMode } from '@/styles/theme';

interface SideBarProps {
  onOpen: (openedState: boolean) => void;
  selectedMenuKey: string;
}

const VerticalMaterialUISwitch = styled(Switch)(({ theme }) => ({
  width: 36, // Swapped from original
  height: 62, // Swapped from original
  padding: 7,
  borderRadius: 20 / 2,
  '& .MuiSwitch-switchBase': {
    margin: 2,
    padding: 1,
    transform: 'translateY(6px)', // Changed from translateX to translateY
    '&.Mui-checked': {
      color: '#fff',
      transform: 'translateY(22px)', // Changed from translateX to translateY
      '& .MuiSwitch-thumb:before': {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          '#fff',
        )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
      },
      '& .MuiSwitch-thumb': {
        backgroundColor: '#F6AE2D',
        width: 32,
        height: 32,
        ...theme.applyStyles('dark', {
          backgroundColor: '#334657',
        }),
      },
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.16)' : 'transparent',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    backgroundColor: '#FFE6B0',
    width: 32,
    height: 32,
    marginTop: -2,
    '&::before': {
      content: "''",
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: 0,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
        'black',
      )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
    },
    ...theme.applyStyles('dark', {
      backgroundColor: '#003892',
    }),
  },
  '& .MuiSwitch-track': {
    opacity: 1,
    backgroundColor: '#F6AE2D',
    borderRadius: 20 / 2,
    ...theme.applyStyles('dark', {
      backgroundColor: '#8796A5',
    }),
  },
}));

export default function SidebarCustomer({
  onOpen,
  selectedMenuKey,
}: SideBarProps) {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const { mode } = useColorMode();
  const callback = () =>
    navigate('/login', {
      replace: true,
    });
  const logout = useLogout({ callback });
  const handleProfileRedirect = () => navigate('/profile');

  const handleSideBarChange = (open: boolean) => {
    onOpen(open);
  };

  const navItems = useMemo(() => {
    const handleLogout = () => logout.mutate({});
    return [
      {
        text: 'Application',
        key: 'Application',
        icon: <ApplicationLogo />,
      },
      {
        text: 'Configurations',
        key: 'Configurations',
        icon: <GMapIcon mode={mode} />,
        onClick: () => navigate('/configurations'),
      },
      {
        text: 'Vehicles',
        key: 'Vehicles',
        icon: <VehicleIcon mode={mode} />,
        onClick: () => navigate('/vehicles'),
      },
      {
        text: 'Permits',
        key: 'Permits',
        icon: <PermitsIcon mode={mode} />,
        onClick: () => navigate('/permits'),
      },
      {
        text: 'Team',
        key: 'Team',
        icon: <TeamIcon mode={mode} />,
        divider: true,
        onClick: () => navigate('/teams'),
      },
      {
        text: 'Logout',
        key: 'Logout',
        icon: <LogoutIcon mode={mode} />,
        onClick: () => handleLogout(),
      },
    ];
  }, [navigate, logout]);

  return (
    <PSideBar
      logoExpanded={<ApplicationLogo />}
      logoCollapsed={<ApplicationLogo />}
      items={navItems}
      childrenCollapsed={
        <CollapsedIcon
          profilePhotoUrl={user?.gravatar}
          handleProfileRedirect={handleProfileRedirect}
          selectedMenuKey={selectedMenuKey}
        />
      }
      onOpenChanged={handleSideBarChange}
      paperProps={{
        sx: {
          '& .MuiList-root': {
            margin: '14px',
          },
          '& .MuiList-root > .MuiButtonBase-root': {
            position: 'relative',
            bottom: '40px',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          },
          '& .MuiList-root > .MuiButtonBase-root.MuiListItemButton-root:first-of-type .MuiBox-root svg':
            {
              position: 'relative',
              bottom: '50px',
              paddingBottom: '0',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            },
          '& .MuiList-root > .MuiButtonBase-root.MuiListItemButton-root:first-of-type .MuiBox-root':
            {
              position: 'relative',
              top: '30px',
            },
          '& .MuiList-root > .MuiListItem-root': {
            position: 'relative !important',
            top: '-25px !important',
          },
          '& .MuiList-root > .MuiListItem-root:nth-of-type(2)': {
            marginTop: '10px !important',
          },
          '& .MuiList-root > .MuiDivider-root': {
            position: 'relative !important',
            bottom: '20px !important',
            width: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.23)',
          },
        },
      }}
      selectedMenuKey={selectedMenuKey}
      defaultOpen={false}
      collapsible={false}
    ></PSideBar>
  );
}

const CollapsedIcon = ({
  profilePhotoUrl,
  handleProfileRedirect,
  selectedMenuKey,
}: {
  profilePhotoUrl: string | undefined;
  handleProfileRedirect: () => void;
  selectedMenuKey: string;
}) => {
  const { toggleColorMode, mode } = useColorMode();

  return (
    <Box py={3} mt={-3}>
      {/* <IconButVton>
        <ArrowAltLeftIcon></ArrowAltLeftIcon>
      </IconButton> */}
      <VerticalMaterialUISwitch
        checked={mode === 'dark' ? true : false}
        onChange={() => toggleColorMode()}
        sx={{ ml: 4 }}
      />
      <ListItemButton
        onClick={handleProfileRedirect}
        sx={{
          ...(selectedMenuKey === 'Profile' && {
            bgcolor: 'rgba(255, 255, 255, 0.16)',
          }),
        }}
      >
        <ListItemIcon>
          <Avatar
            sx={{
              width: '3em',
              height: '3em',
            }}
            src={profilePhotoUrl}
          ></Avatar>
        </ListItemIcon>
      </ListItemButton>
    </Box>
  );
};
